import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  UserPlus,
  Eye,
  EyeOff,
  ChevronRight,
  ChevronLeft,
  Check,
  X,
  Calendar,
  Building,
  Briefcase,
  Server,
  MessageSquare
} from 'lucide-react';
import type {
  BasicSignupData,
  DemoRequestStep1,
  DemoRequestStep2,
  CompleteSignupData
} from '@/types';

/**
 * Signup Page Component
 * Multi-step signup form with enhanced data collection
 */
export function SignupPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Step 1: Basic signup data
  const [basicData, setBasicData] = useState<BasicSignupData>({
    email: '',
    password: '',
    confirmPassword: ''
  });

  // Step 2: Demo request form - page 1
  const [demoStep1, setDemoStep1] = useState<DemoRequestStep1>({
    fullName: '',
    workEmail: '',
    company: '',
    jobTitle: '',
    userType: 'enterprise-buyer',
    userTypeOther: '',
    deploymentInfo: []
  });

  // Step 3: Demo request form - page 2
  const [demoStep2, setDemoStep2] = useState<DemoRequestStep2>({
    engagementTiming: 'immediately',
    wantsDemo: false,
    stayConnected: {
      productUpdates: false,
      ndaAccess: false
    },
    preferredContact: 'email',
    hearAboutUs: [],
    hearAboutUsOther: '',
    additionalInfo: ''
  });

  // Password visibility toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Handle basic data changes
  const handleBasicDataChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setBasicData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  // Handle demo step 1 changes
  const handleDemoStep1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setDemoStep1(prev => ({
      ...prev,
      [id]: value
    }));
  };

  // Handle user type selection
  const handleUserTypeChange = (type: DemoRequestStep1['userType']) => {
    setDemoStep1(prev => ({
      ...prev,
      userType: type
    }));
  };

  // Handle deployment info selection (multi-select)
  const handleDeploymentInfoChange = (item: string) => {
    setDemoStep1(prev => {
      const newDeploymentInfo = prev.deploymentInfo.includes(item)
        ? prev.deploymentInfo.filter(i => i !== item)
        : [...prev.deploymentInfo, item];

      return {
        ...prev,
        deploymentInfo: newDeploymentInfo
      };
    });
  };

  // Handle demo step 2 changes
  const handleDemoStep2Change = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setDemoStep2(prev => ({
      ...prev,
      [id]: value
    }));
  };

  // Handle engagement timing selection
  const handleEngagementTimingChange = (timing: DemoRequestStep2['engagementTiming']) => {
    setDemoStep2(prev => ({
      ...prev,
      engagementTiming: timing
    }));
  };

  // Handle demo preference
  const handleDemoPreferenceChange = (wantsDemo: boolean) => {
    setDemoStep2(prev => ({
      ...prev,
      wantsDemo
    }));
  };

  // Handle stay connected options
  const handleStayConnectedChange = (option: 'productUpdates' | 'ndaAccess') => {
    setDemoStep2(prev => ({
      ...prev,
      stayConnected: {
        ...prev.stayConnected,
        [option]: !prev.stayConnected[option]
      }
    }));
  };

  // Handle preferred contact method
  const handlePreferredContactChange = (method: DemoRequestStep2['preferredContact']) => {
    setDemoStep2(prev => ({
      ...prev,
      preferredContact: method
    }));
  };

  // Handle hear about us selection (multi-select)
  const handleHearAboutUsChange = (item: string) => {
    setDemoStep2(prev => {
      const newHearAboutUs = prev.hearAboutUs.includes(item)
        ? prev.hearAboutUs.filter(i => i !== item)
        : [...prev.hearAboutUs, item];

      return {
        ...prev,
        hearAboutUs: newHearAboutUs
      };
    });
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Validate basic signup data
  const validateBasicData = (): boolean => {
    if (basicData.password !== basicData.confirmPassword) {
      toast.error('Passwords do not match!');
      return false;
    }

    if (basicData.password.length < 6) {
      toast.error('Password must be at least 6 characters long!');
      return false;
    }

    return true;
  };

  // Handle step 1 submission (basic signup)
  const handleStep1Submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateBasicData()) {
      // Pre-fill work email with signup email
      setDemoStep1(prev => ({
        ...prev,
        workEmail: basicData.email
      }));
      setCurrentStep(2);
    }
  };

  // Handle step 2 submission (demo request page 1)
  const handleStep2Submit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentStep(3);
  };

  // Handle final submission (create account)
  const handleFinalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        basicData.email,
        basicData.password
      );
      const user = userCredential.user;

      // Update Firebase user profile with display name from step 2
      const fullName = demoStep1.fullName?.trim();
      if (fullName) {
        try {
          const { updateProfile } = await import('firebase/auth');
          await updateProfile(user, {
            displayName: fullName
          });
          console.log('Firebase profile updated with name:', fullName);
        } catch (profileError) {
          console.warn('Profile update failed:', profileError);
        }
      }

      // Sync user to MongoDB with full name
      try {
        const idToken = await user.getIdToken();
        await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/auth/sync-user`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            idToken,
            fullName: fullName || ''
          }),
        });
        console.log('User synced to MongoDB with name:', fullName);
      } catch (syncError) {
        console.warn('User sync failed:', syncError);
        // Don't fail signup if sync fails
      }

      // Store additional data (in a real app, you'd save this to a database)
      const completeData: CompleteSignupData = {
        ...basicData,
        demoRequest: {
          step1: demoStep1,
          step2: demoStep2
        }
      };

      // Log the complete data (in production, save to database)
      console.log('Complete signup data:', completeData);
      console.log('User created:', user.email);

      // Success toast and redirect
      toast.success('Account created successfully! Thank you for providing additional information. Please log in.');

      // Redirect to login page
      navigate('/login');

    } catch (error: any) {
      // Handle Firebase Auth errors
      let errorMessage = 'An error occurred during signup.';

      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'This email is already registered. Please use a different email or try logging in.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address.';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password is too weak. Please choose a stronger password.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your internet connection.';
          break;
        default:
          errorMessage = error.message || 'An unexpected error occurred.';
      }

      toast.error(errorMessage);
      console.error('Signup error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Skip to final step
  const skipToFinal = () => {
    setCurrentStep(3);
  };

  // Go back to previous step
  const goToPreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
      <div className="w-full max-w-2xl space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <UserPlus className="w-6 h-6 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold mb-2">Welcome to NeurosEdge</h1>
          <p className="text-gray-400">
            {currentStep === 1 && 'Create your account'}
            {currentStep === 2 && 'Tell us about yourself (Optional)'}
            {currentStep === 3 && 'Your preferences (Optional)'}
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex justify-center space-x-4 mb-8">
          {[1, 2, 3].map((step) => (
            <div
              key={step}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === currentStep
                  ? 'bg-blue-600 text-white'
                  : step < currentStep
                  ? 'bg-blue-800 text-blue-200'
                  : 'bg-gray-700 text-gray-400'
              }`}
            >
              {step < currentStep ? <Check className="w-4 h-4" /> : step}
            </div>
          ))}
        </div>

        {/* Step Content */}
        {currentStep === 1 && (
          <BasicSignupStep
            data={basicData}
            onChange={handleBasicDataChange}
            onSubmit={handleStep1Submit}
            showPassword={showPassword}
            showConfirmPassword={showConfirmPassword}
            togglePasswordVisibility={togglePasswordVisibility}
            toggleConfirmPasswordVisibility={toggleConfirmPasswordVisibility}
            isLoading={isLoading}
          />
        )}

        {currentStep === 2 && (
          <DemoRequestStep1Component
            data={demoStep1}
            onChange={handleDemoStep1Change}
            onUserTypeChange={handleUserTypeChange}
            onDeploymentInfoChange={handleDeploymentInfoChange}
            onSubmit={handleStep2Submit}
            onSkip={skipToFinal}
            onBack={goToPreviousStep}
          />
        )}

        {currentStep === 3 && (
          <DemoRequestStep2Component
            data={demoStep2}
            onChange={handleDemoStep2Change}
            onEngagementTimingChange={handleEngagementTimingChange}
            onDemoPreferenceChange={handleDemoPreferenceChange}
            onStayConnectedChange={handleStayConnectedChange}
            onPreferredContactChange={handlePreferredContactChange}
            onHearAboutUsChange={handleHearAboutUsChange}
            onSubmit={handleFinalSubmit}
            onBack={goToPreviousStep}
            isLoading={isLoading}
          />
        )}

        {/* Footer Links */}
        <div className="text-center space-y-4">
          <p className="text-gray-400">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
            >
              Log in
            </Link>
          </p>
          <Link
            to="/"
            className="text-gray-500 hover:text-gray-400 text-sm transition-colors block"
          >
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Basic Signup Step Component
 */
interface BasicSignupStepProps {
  data: BasicSignupData;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  showPassword: boolean;
  showConfirmPassword: boolean;
  togglePasswordVisibility: () => void;
  toggleConfirmPasswordVisibility: () => void;
  isLoading: boolean;
}

function BasicSignupStep({
  data,
  onChange,
  onSubmit,
  showPassword,
  showConfirmPassword,
  togglePasswordVisibility,
  toggleConfirmPasswordVisibility,
  isLoading
}: BasicSignupStepProps) {
  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Email Field */}
      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium text-gray-300">
          Email <span className="text-red-400">*</span>
        </label>
        <Input
          id="email"
          type="email"
          value={data.email}
          onChange={onChange}
          placeholder="Enter your email"
          required
          className="bg-gray-900 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      {/* Password Field */}
      <div className="space-y-2">
        <label htmlFor="password" className="text-sm font-medium text-gray-300">
          Password <span className="text-red-400">*</span>
        </label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={data.password}
            onChange={onChange}
            placeholder="Enter your password"
            required
            className="bg-gray-900 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500 pr-10"
          />
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300"
          >
            {showPassword ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Confirm Password Field */}
      <div className="space-y-2">
        <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-300">
          Confirm Password <span className="text-red-400">*</span>
        </label>
        <div className="relative">
          <Input
            id="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            value={data.confirmPassword}
            onChange={onChange}
            placeholder="Confirm your password"
            required
            className="bg-gray-900 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500 pr-10"
          />
          <button
            type="button"
            onClick={toggleConfirmPasswordVisibility}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300"
          >
            {showConfirmPassword ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={isLoading}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-md transition-colors"
      >
        {isLoading ? (
          <div className="flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
            Processing...
          </div>
        ) : (
          <>
            Continue
            <ChevronRight className="w-4 h-4 ml-2" />
          </>
        )}
      </Button>
    </form>
  );
}

/**
 * Demo Request Step 1 Component
 */
interface DemoRequestStep1Props {
  data: DemoRequestStep1;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onUserTypeChange: (type: DemoRequestStep1['userType']) => void;
  onDeploymentInfoChange: (item: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onSkip: () => void;
  onBack: () => void;
}

function DemoRequestStep1Component({
  data,
  onChange,
  onUserTypeChange,
  onDeploymentInfoChange,
  onSubmit,
  onSkip,
  onBack
}: DemoRequestStep1Props) {
  const userTypeOptions = [
    { value: 'technology-partner', label: 'Technology Partner / Reseller' },
    { value: 'enterprise-buyer', label: 'Enterprise Buyer / Customer' },
    { value: 'analyst-researcher', label: 'Analyst / Researcher' },
    { value: 'press-media', label: 'Press / Media Contact' },
    { value: 'other', label: 'Other' }
  ] as const;

  const deploymentOptions = [
    'ServiceNow',
    'Azure AD / Okta',
    'Terraform',
    'Slack / Teams',
    'JAMF / MDM',
    'None / Not Sure'
  ];

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold mb-2">Tell us about yourself</h2>
        <p className="text-gray-400 text-sm">All fields are optional - you can skip this step</p>
      </div>

      {/* Personal Information */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="fullName" className="text-sm font-medium text-gray-300">
            Full Name
          </label>
          <Input
            id="fullName"
            type="text"
            value={data.fullName}
            onChange={onChange}
            placeholder="Your full name"
            className="bg-gray-900 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="workEmail" className="text-sm font-medium text-gray-300">
            Work Email
          </label>
          <Input
            id="workEmail"
            type="email"
            value={data.workEmail}
            onChange={onChange}
            placeholder="Your work email"
            className="bg-gray-900 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="company" className="text-sm font-medium text-gray-300">
            Company / Organization
          </label>
          <Input
            id="company"
            type="text"
            value={data.company}
            onChange={onChange}
            placeholder="Your company"
            className="bg-gray-900 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="jobTitle" className="text-sm font-medium text-gray-300">
            Job Title
          </label>
          <Input
            id="jobTitle"
            type="text"
            value={data.jobTitle}
            onChange={onChange}
            placeholder="Your job title"
            className="bg-gray-900 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* User Type Selection */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-gray-300">
          I am a: <span className="text-red-400">*</span>
        </label>
        <div className="space-y-2">
          {userTypeOptions.map((option) => (
            <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="userType"
                value={option.value}
                checked={data.userType === option.value}
                onChange={() => onUserTypeChange(option.value)}
                className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-600 focus:ring-blue-500"
              />
              <span className="text-gray-300">{option.label}</span>
            </label>
          ))}
        </div>

        {data.userType === 'other' && (
          <Input
            id="userTypeOther"
            type="text"
            value={data.userTypeOther}
            onChange={onChange}
            placeholder="Please specify"
            className="bg-gray-900 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500 mt-2"
          />
        )}
      </div>

      {/* Deployment Info */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-gray-300">
          Deployment Info (Optional, multi-select)
        </label>
        <div className="grid grid-cols-2 gap-2">
          {deploymentOptions.map((option) => (
            <label key={option} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={data.deploymentInfo.includes(option)}
                onChange={() => onDeploymentInfoChange(option)}
                className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-600 rounded focus:ring-blue-500"
              />
              <span className="text-gray-300 text-sm">{option}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-4">
        <Button
          type="button"
          onClick={onBack}
          variant="outline"
          className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Button
          type="button"
          onClick={onSkip}
          variant="outline"
          className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
        >
          <X className="w-4 h-4 mr-2" />
          Skip
        </Button>

        <Button
          type="submit"
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
        >
          Next
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </form>
  );
}

/**
 * Demo Request Step 2 Component
 */
interface DemoRequestStep2Props {
  data: DemoRequestStep2;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onEngagementTimingChange: (timing: DemoRequestStep2['engagementTiming']) => void;
  onDemoPreferenceChange: (wantsDemo: boolean) => void;
  onStayConnectedChange: (option: 'productUpdates' | 'ndaAccess') => void;
  onPreferredContactChange: (method: DemoRequestStep2['preferredContact']) => void;
  onHearAboutUsChange: (item: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
  isLoading: boolean;
}

function DemoRequestStep2Component({
  data,
  onChange,
  onEngagementTimingChange,
  onDemoPreferenceChange,
  onStayConnectedChange,
  onPreferredContactChange,
  onHearAboutUsChange,
  onSubmit,
  onBack,
  isLoading
}: DemoRequestStep2Props) {
  const engagementOptions = [
    { value: 'immediately', label: 'Immediately' },
    { value: '30-60-days', label: '30–60 Days' },
    { value: 'later-this-year', label: 'Later This Year' },
    { value: 'just-exploring', label: 'Just Exploring' }
  ] as const;

  const contactOptions = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'linkedin', label: 'LinkedIn' }
  ] as const;

  const hearAboutUsOptions = [
    'LinkedIn',
    'Partner',
    'Referral',
    'ServiceNow Ecosystem',
    'Conference or Event',
    'Other'
  ];

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold mb-2">Your preferences</h2>
        <p className="text-gray-400 text-sm">Help us tailor your experience (all optional)</p>
      </div>

      {/* Engagement Timing */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-gray-300">
          <Calendar className="w-4 h-4 inline mr-2" />
          When would you be ready to engage?
        </label>
        <div className="space-y-2">
          {engagementOptions.map((option) => (
            <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="engagementTiming"
                value={option.value}
                checked={data.engagementTiming === option.value}
                onChange={() => onEngagementTimingChange(option.value)}
                className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-600 focus:ring-blue-500"
              />
              <span className="text-gray-300">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Demo Preference */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-gray-300">
          <Server className="w-4 h-4 inline mr-2" />
          Would you like a live demo or technical deep dive?
        </label>
        <div className="space-y-2">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="wantsDemo"
              checked={data.wantsDemo === true}
              onChange={() => onDemoPreferenceChange(true)}
              className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-600 focus:ring-blue-500"
            />
            <span className="text-gray-300">Yes</span>
          </label>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="wantsDemo"
              checked={data.wantsDemo === false}
              onChange={() => onDemoPreferenceChange(false)}
              className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-600 focus:ring-blue-500"
            />
            <span className="text-gray-300">No</span>
          </label>
        </div>
      </div>

      {/* Stay Connected */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-gray-300">
          Stay Connected (Checkboxes):
        </label>
        <div className="space-y-2">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={data.stayConnected.productUpdates}
              onChange={() => onStayConnectedChange('productUpdates')}
              className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-600 rounded focus:ring-blue-500"
            />
            <span className="text-gray-300">Sign me up for early product updates and success stories</span>
          </label>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={data.stayConnected.ndaAccess}
              onChange={() => onStayConnectedChange('ndaAccess')}
              className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-600 rounded focus:ring-blue-500"
            />
            <span className="text-gray-300">I'm open to signing an NDA for roadmap access</span>
          </label>
        </div>
      </div>

      {/* Preferred Contact Method */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-gray-300">
          <MessageSquare className="w-4 h-4 inline mr-2" />
          Preferred contact method
        </label>
        <div className="flex space-x-4">
          {contactOptions.map((option) => (
            <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="preferredContact"
                value={option.value}
                checked={data.preferredContact === option.value}
                onChange={() => onPreferredContactChange(option.value)}
                className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-600 focus:ring-blue-500"
              />
              <span className="text-gray-300">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* How did you hear about us */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-gray-300">
          How did you hear about us? (Multiple choice)
        </label>
        <div className="grid grid-cols-2 gap-2">
          {hearAboutUsOptions.map((option) => (
            <label key={option} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={data.hearAboutUs.includes(option)}
                onChange={() => onHearAboutUsChange(option)}
                className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-600 rounded focus:ring-blue-500"
              />
              <span className="text-gray-300 text-sm">{option}</span>
            </label>
          ))}
        </div>

        {data.hearAboutUs.includes('Other') && (
          <Input
            id="hearAboutUsOther"
            type="text"
            value={data.hearAboutUsOther}
            onChange={onChange}
            placeholder="Please specify"
            className="bg-gray-900 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500 mt-2"
          />
        )}
      </div>

      {/* Additional Info */}
      <div className="space-y-2">
        <label htmlFor="additionalInfo" className="text-sm font-medium text-gray-300">
          Anything else you'd like us to know?
        </label>
        <Textarea
          id="additionalInfo"
          value={data.additionalInfo}
          onChange={onChange}
          placeholder="Tell us more about your needs or questions..."
          rows={4}
          className="bg-gray-900 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      {/* Submit and Get Early Access Section */}
      <div className="text-center space-y-4 py-6 border-t border-gray-700">
        <h3 className="text-lg font-semibold text-white">
          Submit and Get Early Access
        </h3>
        <p className="text-gray-400 text-sm leading-relaxed max-w-lg mx-auto">
          After submitting, you'll receive a confirmation email within 1 business day with next steps,
          including scheduling options for a pilot kickoff, roadmap briefing, or press/demo access.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-4">
        <Button
          type="button"
          onClick={onBack}
          variant="outline"
          className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Button
          type="submit"
          disabled={isLoading}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Creating account...
            </div>
          ) : (
            <>
              <UserPlus className="w-4 h-4 mr-2" />
              Create Account
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
